<script lang="ts">
import { useSignUpEmailPassword } from '@nhost/vue';
import { useLocale } from 'vuetify';

export default {
  setup() {
    const { t } = useLocale();
    const getTranslation = (path: string) => t(path);
    return {
      getTranslation,
    };
  },
  data() {
    return {
      firstName: '',
      lastName: '',
      email: ``,
      password: '',
      confirmPassword: '',
      invalidCredentialsAlert: false,
      passwordMismatchAlert: false,
      emailAlreadyRegisteredAlert: false,
      needsEmailVerification: false,
      signUpEmailPassword: undefined,
      authenticated: false,
      visible: false,
      submitting: false,
    } as any;
  },
  created() {
    const { signUpEmailPassword } = useSignUpEmailPassword();
    signUpEmailPassword('', '', {
      allowedRoles: [],
    });
    this.signUpEmailPassword = signUpEmailPassword as any;
  },

  methods: {
    async handleSubmit(event: Event) {
      event.preventDefault();

      if (this.submitting) return;

      if (this.password !== this.confirmPassword) {
        this.passwordMismatchAlert = true;
        return;
      }

      this.invalidCredentialsAlert = false;
      this.emailAlreadyRegisteredAlert = false;

      const authData = {
        email: this.email,
        password: this.password,
        confirmPassword: this.confirmPassword,
        metadata: {
          firstName: this.firstName,
          lastName: this.lastName,
        },
      };
      console.log(authData);

      const options = {
        displayName: `${this.firstName} ${this.lastName}`,
        defaultRole: 'user',
        locale: 'pt',
        metadata: {
          firstName: this.firstName,
          lastName: this.lastName,
        },
      };

      console.log({ options });

      this.submitting = true;
      try {
        const { isSuccess, needsEmailVerification, error } = await this.signUpEmailPassword(
          this.email,
          this.password,
          options
        );

        console.log({ isSuccess, error });

        if (isSuccess) {
          this.authenticated = true;
        } else {
          if (error && (error.message?.includes('already registered') || error.status === 409 || error.error === 'email-already-in-use')) {
            this.emailAlreadyRegisteredAlert = true;
          } else {
            this.invalidCredentialsAlert = true;
          }
        }
      } finally {
        this.submitting = false;
      }
    },
  },
  watch: {
    authenticated(val) {
      if (val) {
        const pendingInviteToken = sessionStorage.getItem('investorInviteToken');
        if (pendingInviteToken) {
          sessionStorage.removeItem('investorInviteToken');
          this.$router.push(`/investor-invite/${pendingInviteToken}`);
        } else {
          this.$router.push('/app');
        }
      }
    },
  },
};
</script>

<template>
  <div class="sign-up-container">
    <div class="sign-up pa-10 d-flex">
      <div class="w-75">
        <div class="d-flex justify-center">
          <v-img eager style="max-width: 80%" class="mb-10" src="@/assets/logo_cockpit_full_svg.svg" />
        </div>
        <p v-if="needsEmailVerification">
          {{ getTranslation('$vuetify.auth.sign-up.needsEmailVerification.text') }}
        </p>

        <form @submit="handleSubmit" v-else>
          <v-text-field
            variant="outlined"
            v-model="firstName"
            type="text"
            :label="getTranslation('$vuetify.auth.sign-up.first-name.label')"
            density="compact"
            class="input-compact"
          />
          <v-text-field
            variant="outlined"
            v-model="lastName"
            type="text"
            :label="getTranslation('$vuetify.auth.sign-up.last-name.label')"
            density="compact"
            class="input-compact"
          />
          <v-text-field
            variant="outlined"
            v-model="email"
            type="email"
            :label="getTranslation('$vuetify.auth.sign-up.email.label')"
            density="compact"
            class="input-compact"
            />
          <v-text-field
            :append-inner-icon="visible ? 'mdi-eye-off' : 'mdi-eye'"
            :type="visible ? 'text' : 'password'"
            variant="outlined"
            v-model="password"
            @click:append-inner="visible = !visible"
            autocomplete="current-password"
            :label="getTranslation('$vuetify.auth.sign-up.password.label')"
            density="compact"
            class="input-compact"
          />
          <v-text-field
            :append-inner-icon="visible ? 'mdi-eye-off' : 'mdi-eye'"
            :type="visible ? 'text' : 'password'"
            variant="outlined"
            v-model="confirmPassword"
            @click:append-inner="visible = !visible"
            autocomplete="current-password"
            :label="getTranslation('$vuetify.auth.sign-up.confirm-password.label')"
            density="compact"
            class="input-compact"
          />

          <v-alert
            v-model="invalidCredentialsAlert"
            type="error"
            variant="tonal"
            color="danger"
            border="start"
            closable
            :text="getTranslation('$vuetify.auth.sign-in.wrong-credentials.text')"
          />
          <v-alert
            v-model="passwordMismatchAlert"
            type="error"
            variant="tonal"
            color="danger"
            border="start"
            closable
            :text="getTranslation('$vuetify.auth.sign-up.password-mismatch.text')"
          />
          <v-alert
          v-model="emailAlreadyRegisteredAlert"
          type="error"
          variant="tonal"
          color="danger"
          border="start"
          closable
          class="alert-compact"
        >
          <span class="error-msg-small">
            {{ getTranslation('$vuetify.auth.sign-up.email-already-registered.text') }}.
            {{ getTranslation('$vuetify.auth.sign-up.email-already-registered.prefix') }}
            <span class="sign-in-link" @click="$router.push('/sign-in')">
              {{ getTranslation('$vuetify.auth.sign-up.email-already-registered.link') }}
            </span>
          </span>
        </v-alert>

          <v-btn min-width="100%" color="primary" class="text-none" type="submit" :disabled="submitting" :loading="submitting">
            {{ getTranslation('$vuetify.auth.sign-up.sign-up-btn.text') }}
          </v-btn>
          <div class="d-flex justify-end" style="margin-top: 1rem; margin-right: 1rem;">
            <span class="go-back-text" @click="$router.push('/sign-in')">
              {{ getTranslation('$vuetify.auth.sign-up.go-back-btn.text') }}
            </span>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.sign-up-container {
  height: 100vh;
  overflow-y: auto;
  scrollbar-width: none;  /* Firefox */
  -ms-overflow-style: none;  /* IE and Edge */
  &::-webkit-scrollbar {
    display: none;  /* Chrome, Safari and Opera */
  }
}

.sign-up {
  justify-content: center;
  align-items: center;
  text-align: center;
  min-height: 100%;
}

.go-back-text {
  color: rgb(var(--v-theme-primary));
  cursor: pointer;
}

.input-compact {
  margin-bottom: 0.5rem !important;
  font-size: 0.95rem !important;
}
.sign-in-link {
  text-decoration: underline;
  cursor: pointer;
}
.alert-compact {
  min-width: 0 !important;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
  padding-left: 12px !important;
  padding-right: 12px !important;
}
</style>
